import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useInView } from "react-intersection-observer";

import useCustomerService from "../../services/api/customerService";
import { enums } from "../../constants/enum";
import dateFormatter from "../../util/dateFormatter";

import {
  AddIcon,
  ArrowLongRightIcon,
  CloseIcon,
  SearchIcon,
} from "../../assets/icons";
import { Button, Loading, PageTitle, Section } from "../../components";
import { SelectInput, TextInput } from "../../components/forms";

import "./CustomersPage.scss";

function CustomersPage() {
  const navigate = useNavigate();
  const { ref, inView } = useInView();
  const [type, setType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { queryKey, getAllCustomers } = useCustomerService();
  const { data, isLoading, error, isFetchingNextPage, fetchNextPage, refetch } =
    useInfiniteQuery({
      queryKey: [queryKey, { searchQuery, type }],
      queryFn: ({ pageParam = 1 }) =>
        getAllCustomers(searchQuery, type, pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
      retry: 1,
    });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage]);

  if (isLoading) return <Loading variant="fullScreen" />;
  if (error) {
    toast.error(
      error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
    );
    navigate(-1, { replace: true });
  }

  const handleFilter = (values) => {
    setSearchQuery(values.query);
    setType(values.type);
    refetch();
  };
  return (
    <div className="page customers-page">
      <PageTitle text="Customers">
        <Link to="/customers/add">
          <Button icon={<AddIcon />} text="Add new customer" />
        </Link>
      </PageTitle>

      <Section className="customer-details" title="All Customers">
        <div className="customer-search">
          <Formik
            enableReinitialize
            initialValues={{ query: searchQuery, type }}
            onSubmit={handleFilter}
          >
            <Form>
              <TextInput
                type="search"
                name="query"
                label="Search by name or phone number"
                placeholder="Enter customer name or phone number"
              />
              <SelectInput
                name="type"
                values={[
                  { label: "All", value: "" },
                  { label: "Eye Camp", value: enums.CUSTOMER_TYPES.EYE_CAMP },
                  { label: "Normal", value: enums.CUSTOMER_TYPES.NORMAL },
                ]}
                label="Customer type"
              />
              <div className="form-actions">
                <Button type="submit" icon={<SearchIcon />} text="Search" />
                <Button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setType("");
                    refetch();
                  }}
                  icon={<CloseIcon />}
                  text="Clear"
                  outlined
                  className="clear-btn"
                />
              </div>
            </Form>
          </Formik>
        </div>

        <div className="customer-list">
          <div className="table-title">
            <h3>All Shop Customers</h3>
          </div>

          <div className="table-content">
            <table>
              <thead>
                <tr>
                  <th>Customer ID</th>
                  <th>Name</th>
                  <th>Mobile Number</th>
                  <th>Last Order</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {data?.pages.map(({ customers }) =>
                  customers.map((customer) => (
                    <tr key={customer?._id}>
                      <td>{customer?._id}</td>
                      <td>{customer?.nameWithTitle}</td>
                      <td>{customer?.mobileNumber}</td>
                      <td>
                        {customer?.lastOrderDate
                          ? dateFormatter(customer?.lastOrderDate)
                          : "N/A"}
                      </td>
                      <td>
                        <Link to={`/customers/details/${customer?._id}`}>
                          <Button
                            text="View"
                            prefixIcon={<ArrowLongRightIcon />}
                          />
                        </Link>
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              ref={ref}
            >
              <Loading isLoading={isFetchingNextPage} variant="inline" />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export default CustomersPage;
